import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {
  type SlurmDaemonReadinessByRunNameQuery,
  useSlurmDaemonReadinessByRunNameQuery,
} from '../../generated/graphql';
import {Banner, BannerBoldText} from '../Banner';

export const DAEMON_CHECK_IN_STALE_AFTER_MINUTES = 10;
export const DAEMON_CHECK_IN_STALE_AFTER_MS =
  DAEMON_CHECK_IN_STALE_AFTER_MINUTES * 60 * 1000;

interface SlurmHealthProps {
  projectName: string;
  entityName: string;
  runName: string;
  skip?: boolean;
}

type SlurmHealthStatus =
  | {type: 'loading'}
  | {type: 'not-detected'}
  | {type: 'healthy'}
  | {type: 'stale'}
  | {type: 'unhealthy'; message?: string | null};

type SlurmHealthQueryHealth = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<SlurmDaemonReadinessByRunNameQuery['project']>['run']
    >['slurmControl']
  >['health']
>;

export function getSlurmHealthStatus({
  loading,
  health,
}: {
  loading: boolean;
  health: SlurmHealthQueryHealth | null | undefined;
}): SlurmHealthStatus {
  if (loading) {
    return {type: 'loading'};
  }

  if (health == null || health.lastSeenAt == null) {
    return {type: 'not-detected'};
  }

  const lastSeenAtMs = Date.parse(health.lastSeenAt);
  const isStale =
    Number.isNaN(lastSeenAtMs) ||
    Date.now() - lastSeenAtMs > DAEMON_CHECK_IN_STALE_AFTER_MS;

  if (isStale) {
    return {type: 'stale'};
  }

  if (!health.ready) {
    return {type: 'unhealthy', message: health.message};
  }

  return {type: 'healthy'};
}

export function SlurmHealth({
  projectName,
  entityName,
  runName,
  skip,
}: SlurmHealthProps) {
  const {data, loading} = useSlurmDaemonReadinessByRunNameQuery({
    variables: {
      projectName,
      entityName,
      runName,
    },
    skip,
    fetchPolicy: 'network-only',
  });

  const currentStatus = getSlurmHealthStatus({
    loading,
    health: data?.project?.run?.slurmControl?.health,
  });

  return (
    <Tailwind>
      <div className="py-8 text-sm text-wb-text-tertiary">
        {currentStatus.type === 'not-detected' && (
          <Banner variant="warning">
            <span>
              <BannerBoldText>Slurm daemon not detected.</BannerBoldText> Make
              sure the daemon is running.
            </span>
          </Banner>
        )}
        {currentStatus.type === 'unhealthy' && (
          <Banner variant="warning">
            <div>
              <div>
                <BannerBoldText>Slurm daemon error.</BannerBoldText>{' '}
                {currentStatus.message ?? 'Check the daemon logs for details.'}
              </div>
              <div>You can still submit or queue this command.</div>
            </div>
          </Banner>
        )}
        {currentStatus.type === 'stale' && (
          <Banner variant="warning">
            <div>
              <div>
                <BannerBoldText>Slurm daemon is not responding.</BannerBoldText>{' '}
                Last check-in was more than{' '}
                {DAEMON_CHECK_IN_STALE_AFTER_MINUTES} minutes ago.
              </div>
              <div>You can still submit or queue this command.</div>
            </div>
          </Banner>
        )}
      </div>
    </Tailwind>
  );
}
